import { focusManager, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import {
  dependentKeys,
  entityKeys,
  resolveInvalidationKeys,
  type Facet,
  type InvalidationEntity,
} from "../entityDependencies";
import { NON_ORG_SCOPED_QUERY_KEYS } from "../queryKeys";

/**
 * Refresh for changes that happen outside a mutation of this app (a webhook,
 * a finished pipeline, a batch of raw uploads): reconciles the entities in
 * the background, never awaited.
 */
export function useRefreshEntities(orgId: string) {
  const client = useQueryClient();
  return useCallback(
    (
      entities: readonly InvalidationEntity[],
      options: { facets?: readonly Facet[]; keys?: readonly QueryKey[] } = {},
    ) => {
      const { facets, keys = [] } = options;
      const entityPrefixes = facets
        ? entities.flatMap((entity) => [
            ...entityKeys(entity, orgId, facets),
            ...dependentKeys(entity, orgId),
          ])
        : resolveInvalidationKeys(entities, orgId);
      for (const queryKey of [...entityPrefixes, ...keys])
        void client.invalidateQueries({ queryKey });
    },
    [client, orgId],
  );
}

/** How often a watched screen re-reads what it shows. */
export const LIVE_REFRESH_INTERVAL = 30_000;

/**
 * Keeps a watched screen current with the rest of the team: on a fixed cadence
 * it re-reads the entity and everything derived from it, while the tab is
 * visible and no write of this app is in flight.
 */
export function useLiveRefresh(orgId: string, entity: InvalidationEntity) {
  const client = useQueryClient();
  useEffect(() => {
    if (!orgId) return;
    const timer = setInterval(() => {
      if (!focusManager.isFocused() || client.isMutating() > 0) return;
      for (const queryKey of resolveInvalidationKeys([entity], orgId))
        void client.invalidateQueries({ queryKey }, { cancelRefetch: false });
    }, LIVE_REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [client, orgId, entity]);
}

const isOrganizationScoped = (top: unknown) =>
  !NON_ORG_SCOPED_QUERY_KEYS.some((key) => key === top);

/** Session-wide cache operations: login, logout, session expiry, organization switch. */
export function useSessionCache() {
  const client = useQueryClient();
  return useMemo(
    () => ({
      clear: () => client.clear(),
      halt: () => client.cancelQueries(),
      dropOrganizationData: () =>
        client.removeQueries({ predicate: (query) => isOrganizationScoped(query.queryKey[0]) }),
    }),
    [client],
  );
}
